import React from 'react';
import { Controller, FieldError } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import useCreateMenuItem from './createMenuItem-hook';
import { useDirectoriesHook } from '../../../../../../Hooks/hooks';
import { ButtonTypes, CreateFormTypes, MenuItemOpionFormTypes } from '../../../../../../Interfaces/componentTypes';
import { ICreateMenuItemForm } from '../../../../../../Interfaces/formValue-interfaces';
import OptionForm from './OptionForm/optionForm';
import FileUploader from '../../../../../../Components/FileUploader/fileUploader';
import AuthInput from '../../../../../../Components/Inputs/AuthInput/authInput';
import useLanguagesHooks from '../../../../../../Components/Languages/setLanguage-hooks';
import TextArea from '../../../../../../Components/Inputs/TextArea/textArea';
import Button from '../../../../../../Components/Button/button';
import AutoComplete from '../../../../../../Components/Inputs/AutoComplete/autoComplete';
import { UploadImage } from '../../../../../../Assets/Images';
import styles from './createMenu.module.scss';

export interface ICreateMenuItemDialogProps {
  companyId: number,
  categoryId: number,
  title: string,
  onClose: () => void,
  cb: () => void
};

const CreateMenuDialog: React.FC<ICreateMenuItemDialogProps> = (props) => {
  const {  title, onClose } = props;
  const {
    fields,
    errors,
    control,
    imageUrl,
    dialogRef,
    activeForm,
    imageLoading,
    isFormChanged,
    updateLoading,
    createLoading,
    menuItemResponce,
    createValidation,
    update,
    onUpdate,
    setValue,
    register,
    onCreate,
    addOption,
    getValues,
    deleteOption,
    handleFileInputChange,
  } = useCreateMenuItem(props);
  const { activeLangSrc, t, activeLanguage, checkableLanguages } = useLanguagesHooks();
  const { timeUnits } = useDirectoriesHook();

  return (
    <div className={styles.container} ref={dialogRef}>
      <div className={styles.header}>
        <p>{title}</p>
      </div>
      <form className={styles.form}>
        {
          activeForm === CreateFormTypes.CREATE
            ?
            <>
              <div className={styles.item}>
                <div className={styles.uploadContainer}>
                  <div className={styles.label}>
                    <p>{t('My_Business.Create_Menu.Image')}</p>
                  </div>
                  <div className={styles.imageBox}>
                    {
                      imageLoading
                        ?
                        <ClipLoader color="#aa002b" size={40} />
                        :
                        <img
                          src={imageUrl ?? UploadImage}
                          alt={`${imageUrl}.png`}
                          className={styles.image}
                        />
                    }
                  </div>
                  <FileUploader
                    id='logo'
                    handleFileInputChange={handleFileInputChange}
                    buttonTitleStyle={styles.buttonTitle}
                    buttonStyle={`${styles.uploadButton}`}
                  />
                </div>
              </div>
              <div className={styles.item}>
                <div className={styles.label}>
                  <img src={activeLangSrc} alt={`${activeLangSrc}.png`} />
                  <p>
                    {t('My_Business.Create_Menu.Name')}
                  </p>
                </div>
                <AuthInput
                  register={register}
                  registerName='name'
                  labelStyle={styles.inputLabel}
                  error={errors.name}
                  patternValue={/.*[A-Za-zÀ-ÖØ-öø-ÿԱ-Ֆա-ֆА-Яа-яҐґЄєІіЇїΆ-ώΑ-ωא-ת一-龯ぁ-ゔァ-ヾㄱ-ㅎㅏ-ㅣ가-힣].*/}
                />
              </div>
              <div className={styles.item}>
                <div className={styles.label}>
                  <img src={activeLangSrc} alt={`${activeLangSrc}.png`} />
                  <p>
                    {t('My_Business.Create_Menu.Description')}
                  </p>
                </div>
                <TextArea
                  register={register}
                  registerName='description'
                  labelStyle={styles.inputLabel}
                  error={errors.description}
                  patternValue={/.*[A-Za-zÀ-ÖØ-öø-ÿԱ-Ֆա-ֆА-Яа-яҐґЄєІіЇїΆ-ώΑ-ωא-ת一-龯ぁ-ゔァ-ヾㄱ-ㅎㅏ-ㅣ가-힣].*/}
                  required={false}
                />
              </div>
              <div className={styles.formItem}>
                <div className={styles.formItemLabel}>
                  <p>
                    {t('My_Business.Create_Menu.Option_Form.Label')}
                  </p>
                </div>
                <OptionForm
                  type={MenuItemOpionFormTypes.CREATE}
                  options={fields}
                  register={register}
                  control={control}
                  update={update}
                  errors={errors}
                  getValues={getValues}
                  setValue={setValue}
                  addOption={addOption}
                  deleteOption={deleteOption}
                />
              </div>
              <div className={styles.formItem}>
                <div className={styles.formItemLabel}>
                  <p>{t('My_Business.Create_Menu.Cooking_Time.Label')}</p>
                </div>
                <div className={styles.formItemBox}>
                  <div className={styles.formItemContent}>
                    <Controller
                      control={control}
                      name='cooking_time_unit'
                      render={({ field: { onChange, name, value } }) => {
                        return (
                          <AutoComplete
                            value={value}
                            name={name}
                            onChange={onChange}
                            placeholder={t('My_Business.Create_Menu.Cooking_Time.Type')}
                            id='cooking_time_unit'
                            data={timeUnits}
                            error={errors.cooking_time_unit}
                          />
                        );
                      }}
                    />
                    <AuthInput
                      register={register}
                      registerName='cooking_time'
                      labelStyle={styles.inputLabel}
                      placeholder={t('My_Business.Create_Menu.Cooking_Time.Value')}
                      type='number'
                      error={errors.cooking_time}
                      required={false}
                    />
                  </div>
                </div>
                <div className={styles.buttonsRow}>
                  <Button
                    buttonType={ButtonTypes.Secondary}
                    title={t('Buttons.Close')}
                    type='button'
                    onClick={onClose}
                    buttonStyle={styles.closeButton}
                  />
                  <Button
                    buttonType={createValidation ? ButtonTypes.Primery : ButtonTypes.Disabled}
                    title={t('Buttons.Create')}
                    type='button'
                    onClick={onCreate}
                    isLoading={createLoading}
                    buttonStyle={styles.button}
                    disable={!createValidation}
                  />
                </div>
              </div>
            </>
            :
            <>
              {menuItemResponce ?
                <div>
                  <div className={styles.saveFormHeader}>
                    <p>
                      {t('My_Business.Create_Menu.Save_Form.Header')}
                    </p>
                  </div>
                  <div className={styles.item}>
                    <div className={styles.label}>
                      <img src={activeLangSrc} alt={`${activeLangSrc}.png`} />
                      <p>
                        {t('My_Business.Create_Category.Title')}
                      </p>
                    </div>
                    <AuthInput
                      register={register}
                      registerName={`name_${activeLanguage}`}
                      labelStyle={styles.inputLabel}
                      error={errors[`name_${activeLanguage}` as keyof ICreateMenuItemForm] as FieldError | undefined}
                    />
                  </div>
                  {
                    menuItemResponce.description_en
                    &&
                    <div className={styles.item}>
                      <div className={styles.label}>
                        <img src={activeLangSrc} alt={`${activeLangSrc}.png`} />
                        <p>
                          {t('My_Business.Create_Menu.Save_Form.Description')}
                        </p>
                      </div>
                      <TextArea
                        rows={2}
                        register={register}
                        registerName={`description_${activeLanguage}`}
                        labelStyle={styles.inputLabel}
                        error={errors[`description_${activeLanguage}` as keyof ICreateMenuItemForm] as FieldError | undefined}
                      />
                    </div>
                  }
                  {
                    checkableLanguages.map((item) => {
                      return (
                        <>
                          <div className={styles.item} key={item.id}>
                            <div className={styles.label}>
                              <img src={item.icon} alt={`${item.icon}.png`} />
                              <p>
                                {t('My_Business.Create_Category.Title')}
                              </p>
                            </div>
                            <AuthInput
                              register={register}
                              registerName={`name_${item.code}`}
                              labelStyle={styles.inputLabel}
                              error={errors[`name_${item.code}` as keyof ICreateMenuItemForm] as FieldError | undefined}
                            />
                          </div>
                          {
                            menuItemResponce.description_en
                            &&
                            <div className={styles.item}>
                              <div className={styles.label}>
                                <img src={item.icon} alt={`${item.icon}.png`} />
                                <p>
                                   {t('My_Business.Create_Menu.Save_Form.Description')}
                                </p>
                              </div>
                              <TextArea
                                rows={2}
                                register={register}
                                registerName={`description_${item.code}`}
                                labelStyle={styles.inputLabel}
                                error={errors[`description_${item.code}` as keyof ICreateMenuItemForm] as FieldError | undefined}
                              />
                            </div>
                          }
                        </>
                      )
                    })
                  }
                  <div className={styles.buttonsRow}>
                    <Button
                      buttonType={ButtonTypes.Secondary}
                      title={t('Buttons.Close')}
                      type='button'
                      onClick={onClose}
                      buttonStyle={styles.closeButton}
                    />
                    <Button
                      buttonType={isFormChanged && createValidation ? ButtonTypes.Primery : ButtonTypes.Disabled}
                      title={t('Buttons.Save')}
                      type='button'
                      onClick={onUpdate}
                      isLoading={updateLoading}
                      buttonStyle={styles.button}
                      disable={!isFormChanged}
                    />
                  </div>
                </div>
                :
                <ClipLoader color="#aa002b" size={40} />
              }
            </>
        }
      </form>
    </div>
  )
}

export default CreateMenuDialog
